import { useState } from 'react'
import { Package, RefreshCw, Mail } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Badge } from '../../lib/shadcn/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../lib/shadcn/dropdown-menu'
import { cn } from '../../lib/shadcn/utils'
import { toast } from '../../lib/shadcn/sonner'
import { useRefreshTrackingStatus } from '../../hooks/backend/scanPage'

export interface OrderShipment {
  id: string | number
  order_id: string
  carrier: string | null
  service_level: string | null
  tracking_number: string | null
  tracking_status: string | null
  label_url: string
  amount: string | number | null
}

function statusBadgeClass(status: string | null): string {
  // Shippo sends codes like PRE_TRANSIT; Envia sends words like "In Transit".
  const normalized = (status ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_')
  if (normalized === 'DELIVERED') {
    return 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200'
  }
  if (['TRANSIT', 'IN_TRANSIT', 'PRE_TRANSIT', 'PICKED_UP', 'OUT_FOR_DELIVERY'].includes(normalized)) {
    return 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200'
  }
  if (normalized === 'RETURNED' || normalized === 'FAILURE') {
    return 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200'
  }
  return ''
}

function formatStatus(status: string | null): string {
  if (!status) return 'Unknown'
  return status
    .toLowerCase()
    .split('_')
    .map(word => (word.length > 0 ? word[0]!.toUpperCase() + word.slice(1) : word))
    .join(' ')
}

export default function OrderLabelsMenu({
  orderId,
  shipments,
  onRefreshed,
  onEmailTracking,
}: {
  orderId: string
  shipments: OrderShipment[]
  onRefreshed: () => void
  onEmailTracking?: () => void
}) {
  const refreshTrackingStatus = useRefreshTrackingStatus()
  const [refreshing, setRefreshing] = useState(false)

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      await refreshTrackingStatus.trigger({ order_id: orderId })
      onRefreshed()
    } catch {
      toast.error('Failed to refresh tracking status')
    } finally {
      setRefreshing(false)
    }
  }

  const total = shipments.reduce((sum, s) => sum + Number(s.amount ?? 0), 0)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={e => e.stopPropagation()}
        >
          <Package className="w-3 h-3 mr-1" />
          Labels
          <Badge variant="secondary" className="ml-1">{shipments.length}</Badge>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={e => e.stopPropagation()} className="min-w-[260px]">
        <div className="flex items-center justify-between gap-2 px-2 py-1">
          <DropdownMenuLabel className="font-normal text-xs text-muted-foreground p-0">
            Total: ${total.toFixed(2)}
          </DropdownMenuLabel>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-1.5 text-xs text-muted-foreground hover:text-foreground"
            disabled={refreshing}
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
              void handleRefresh()
            }}
          >
            <RefreshCw className={cn('w-3 h-3', refreshing && 'animate-spin')} />
          </Button>
        </div>
        <DropdownMenuSeparator />
        {shipments.map((shipment, i) => (
          <DropdownMenuItem key={shipment.id} asChild>
            <a
              href={shipment.label_url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 w-full cursor-pointer"
            >
              <span className="flex-1">
                Package {i + 1}: {shipment.carrier ?? 'Label'}
                {shipment.tracking_number ? ` — ${shipment.tracking_number}` : ''}
              </span>
              <Badge variant="outline" className={cn('shrink-0 font-normal', statusBadgeClass(shipment.tracking_status))}>
                {formatStatus(shipment.tracking_status)}
              </Badge>
              {shipment.amount !== null && (
                <span className="text-xs text-muted-foreground shrink-0">
                  ${Number(shipment.amount).toFixed(2)}
                </span>
              )}
            </a>
          </DropdownMenuItem>
        ))}
        {onEmailTracking && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                onEmailTracking()
              }}
              className="cursor-pointer"
            >
              <Mail className="w-3.5 h-3.5 mr-2" />
              Email tracking to customer
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
